import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";

/**
 * Push notification permission result.
 */
export interface PushPermissionResult {
  receive: "granted" | "denied" | "prompt" | "prompt-with-rationale";
}

/**
 * Token received on registration.
 */
export interface PushToken {
  value: string;
}

/**
 * Push notification schema.
 * Note: data is typed as `any` to match @capacitor/push-notifications behavior
 */
export interface PushNotificationSchema {
  id?: string;
  title?: string;
  subtitle?: string;
  body?: string;
  data: any;
}

/**
 * Push notification action performed payload.
 */
export interface PushNotificationActionPerformed {
  notification: PushNotificationSchema;
  actionId: string;
  inputValue?: string;
}

/**
 * Listener handle for managing push notification event listeners.
 */
export interface PushNotificationListenerHandle {
  remove: () => Promise<void>;
}

/**
 * Requests push notification permissions.
 * On native platforms, prompts the user for notification permissions.
 * On web, returns denied.
 *
 * @returns Permission result
 */
export async function requestPushPermissions(): Promise<PushPermissionResult> {
  if (!Capacitor.isNativePlatform()) {
    return { receive: "denied" };
  }

  return await PushNotifications.requestPermissions();
}

/**
 * Registers the device for push notifications.
 * On native platforms, registers with APNs/FCM.
 * On web, this is a no-op.
 */
export async function registerPushNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await PushNotifications.register();
}

/**
 * Adds a listener for push notification registration.
 * On native platforms, listens for registration token events.
 * On web, returns a no-op listener handle.
 *
 * @param callback - Callback invoked when registration completes
 * @returns Listener handle
 */
export async function addPushRegistrationListener(
  callback: (token: PushToken) => void
): Promise<PushNotificationListenerHandle> {
  if (!Capacitor.isNativePlatform()) {
    return {
      remove: async () => {
        // No-op for web platform
      },
    };
  }

  return await PushNotifications.addListener("registration", callback);
}

/**
 * Adds a listener for push notification registration errors.
 * On native platforms, listens for registration error events.
 * On web, returns a no-op listener handle.
 *
 * @param callback - Callback invoked on registration error
 * @returns Listener handle
 */
export async function addPushRegistrationErrorListener(
  callback: (error: unknown) => void
): Promise<PushNotificationListenerHandle> {
  if (!Capacitor.isNativePlatform()) {
    return {
      remove: async () => {
        // No-op for web platform
      },
    };
  }

  return await PushNotifications.addListener("registrationError", callback);
}

/**
 * Adds a listener for received push notifications (foreground).
 * On native platforms, listens for push notifications received while app is in foreground.
 * On web, returns a no-op listener handle.
 *
 * @param callback - Callback invoked when a notification is received
 * @returns Listener handle
 */
export async function addPushNotificationReceivedListener(
  callback: (notification: PushNotificationSchema) => void
): Promise<PushNotificationListenerHandle> {
  if (!Capacitor.isNativePlatform()) {
    return {
      remove: async () => {
        // No-op for web platform
      },
    };
  }

  return await PushNotifications.addListener("pushNotificationReceived", callback);
}

/**
 * Adds a listener for push notification actions performed.
 * On native platforms, listens for when user taps on a notification.
 * On web, returns a no-op listener handle.
 *
 * @param callback - Callback invoked when a notification action is performed
 * @returns Listener handle
 */
export async function addPushNotificationActionPerformedListener(
  callback: (action: PushNotificationActionPerformed) => void
): Promise<PushNotificationListenerHandle> {
  if (!Capacitor.isNativePlatform()) {
    return {
      remove: async () => {
        // No-op for web platform
      },
    };
  }

  return await PushNotifications.addListener("pushNotificationActionPerformed", callback);
}
